import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import ItemCreation from './components/ItemCreation';
import ItemsList from './components/ItemsList';
import ItemDetail from './components/ItemDetail';
import { squareAPI } from './services/squareAPI';
import { loadSampleData, clearAllData } from './utils/sampleData';

function App() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const fetchedItems = await squareAPI.getItems();
      setItems(fetchedItems);
      setShowWelcome(fetchedItems.length === 0);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSampleData = () => {
    const sampleItems = loadSampleData();
    setItems(sampleItems);
    setShowWelcome(false);
    alert('Sample data loaded successfully! You can now explore the features.');
  };

  const handleClearAllData = () => {
    if (window.confirm('This will delete all items. Are you sure?')) {
      clearAllData();
      setItems([]);
      setShowWelcome(true);
    }
  };

  const addItem = async (item) => {
    try {
      const newItem = await squareAPI.createItem(item);
      setItems([...items, newItem]);
      return newItem;
    } catch (error) {
      console.error('Error adding item:', error);
      throw error;
    }
  };

  const updateItem = async (id, updates) => {
    try {
      const updatedItem = await squareAPI.updateItem(id, updates);
      setItems(items.map(item => item.id === id ? updatedItem : item));
      return updatedItem;
    } catch (error) {
      console.error('Error updating item:', error);
      throw error;
    }
  };

  const deleteItem = async (id) => {
    try {
      await squareAPI.deleteItem(id);
      setItems(items.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error deleting item:', error);
      throw error;
    }
  };

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        {/* Navigation */}
        <nav className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex space-x-8">
                <Link
                  to="/"
                  className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-900 border-b-2 border-transparent hover:border-gray-300"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Inventory
                </Link>
                <Link
                  to="/create"
                  className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-900 border-b-2 border-transparent hover:border-gray-300"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Item
                </Link>
              </div>
              <div className="flex items-center">
                <span className="text-sm text-gray-500">
                  {items.length} items in inventory
                </span>
                {items.length > 0 && (
                  <button
                    onClick={handleClearAllData}
                    className="ml-4 text-xs text-red-600 hover:text-red-700"
                    title="Clear all data"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {showWelcome && !loading ? (
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-white rounded-lg shadow-lg p-12">
                <svg className="mx-auto h-16 w-16 text-blue-600 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Welcome to Your Inventory System! 👋
                </h2>
                
                <p className="text-lg text-gray-600 mb-8">
                  Get started by loading sample data to explore features, or create your first item.
                </p>

                <div className="space-y-4">
                  <button
                    onClick={handleLoadSampleData}
                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Load Sample Data (5 Items)
                  </button>
                  
                  <Link
                    to="/create"
                    className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Your First Item
                  </Link>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Key Features:</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Multi-step item creation
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Automatic swing tags
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Color & size variations
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Square API integration
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Routes>
              <Route
                path="/"
                element={
                  <ItemsList
                    items={items}
                    loading={loading}
                    onRefresh={loadItems}
                    onDelete={deleteItem}
                  />
                }
              />
              <Route
                path="/create"
                element={<ItemCreation onItemCreated={addItem} />}
              />
              <Route
                path="/item/:id"
                element={
                  <ItemDetail
                    items={items}
                    onUpdate={updateItem}
                    onDelete={deleteItem}
                  />
                }
              />
            </Routes>
          )}
        </main>
      </div>
    </Router>
  );
}

export default App;
